// Helpers para mostrar las fotos de los remitos más rápido.
//
// Las fotos se guardan en Cloudinary en su tamaño original (varios MB).
// Para MOSTRARLAS le pedimos a Cloudinary una versión optimizada agregando
// parámetros a la URL. La URL original guardada en la base NO se modifica
// (el borrado en Cloudinary depende de ese formato).

// Visor: formato automático (WebP/AVIF), calidad automática y máximo 1600px de ancho
export const TAMANIO_VISOR = "f_auto,q_auto,c_limit,w_1600";

// Miniaturas: recorte cuadrado de 200px
export const TAMANIO_MINIATURA = "f_auto,q_auto,c_fill,w_200,h_200";

// Devuelve la URL absoluta de una imagen (las viejas guardadas en el servidor
// vienen como ruta relativa)
export const urlCompleta = (img) => {
  if (!img) return "";
  return img.startsWith("http") ? img : `${process.env.REACT_APP_BASE_URL}/${img}`;
};

// Devuelve la URL optimizada si es de Cloudinary; si no, la devuelve igual
export const urlOptimizada = (img, transformacion = TAMANIO_VISOR) => {
  const url = urlCompleta(img);
  if (!url.includes("res.cloudinary.com") || !url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/${transformacion}/`);
};

// Descarga una imagen en segundo plano para que después aparezca al instante
export const precargarImagen = (img, transformacion = TAMANIO_VISOR) => {
  if (!img) return;
  const pre = new Image();
  pre.src = urlOptimizada(img, transformacion);
};

// ─────────────────────────────────────────────────────────────
// COMPRESIÓN ANTES DE SUBIR
// Achica la foto del celular (lado mayor máx. 2000px, JPEG al 80%)
// para que guardar un servicio desde el campo sea mucho más rápido.
// Si algo falla, o si la foto ya era liviana, devuelve la ORIGINAL:
// nunca se pierde una foto.
// ─────────────────────────────────────────────────────────────
const LADO_MAXIMO = 2000;
const CALIDAD_JPEG = 0.8;

const cargarImagen = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

export const comprimirImagen = async (file) => {
  try {
    if (!file || !file.type || !file.type.startsWith("image/") || file.type === "image/gif") return file;

    // El <img> ya aplica la rotación del celular (EXIF), así las fotos verticales no salen giradas
    const img = await cargarImagen(file);
    const ancho = img.naturalWidth;
    const alto = img.naturalHeight;
    if (!ancho || !alto) return file;

    const escala = Math.min(1, LADO_MAXIMO / Math.max(ancho, alto));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(ancho * escala);
    canvas.height = Math.round(alto * escala);

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; // fondo blanco por si la imagen tenía transparencia
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", CALIDAD_JPEG));
    if (!blob || blob.size >= file.size) return file; // no mejoró: dejamos la original

    const nombre = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], nombre, { type: "image/jpeg", lastModified: Date.now() });
  } catch (error) {
    console.warn("No se pudo comprimir la imagen, se sube la original:", error);
    return file;
  }
};

// Comprime varias fotos (FileList o array)
export const comprimirImagenes = (files) => Promise.all(Array.from(files || []).map(comprimirImagen));
